import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { documentsTypeDisabilities, ListEps, ListInsurances, StatusOptions, TypesOfContingencies, TypesOfDisabilities } from './data.constants';
import { CandidateDisabilitiesService } from '../../services/candidate-disabilities.service';
import { calendarES } from '../../../../theme/functions/calendarES';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '../../../../theme/services/notifications/notifications.service';

@Component({
  selector: "app-candidate-disabilities",
  templateUrl: "./candidate-disabilities.component.html",
  styleUrls: ["./candidate-disabilities.component.scss"],
})
export class CandidateDisabilitiesComponent implements OnInit {
  @ViewChild("gb") searchFilter;

  public typesOfDisabilities = TypesOfDisabilities;
  public documentsTypeDisabilities = documentsTypeDisabilities;
  public typesOfContingencies = TypesOfContingencies;
  public StatusOptions = StatusOptions;
  public listEps = ListEps;
  public listInsurances = ListInsurances;
  public disabilities: any[];
  public totalDisabilities: number;
  public candidateId: number;
  public per_page: number;
  public isLoading: boolean = true;
  public selectedInsurance: string;
  public isArl: boolean = false;
  public isEps: boolean = false;
  public modViewDialogDocumentation: boolean = false;
  public selectedDisabilitie: string | null = null;
  public formDocuments: FormGroup;
  public formFilter: FormGroup;
  public es: any;
  public page: number;
  public sort: string;
  public promiseForm: Promise<any>;
  public isEditSavedDocument: boolean = false;
  public idDisability: number;
  public formGroupNameDocuments: string = "documents";
  public dialogVisibilityKey: string | null = null;

  constructor(
    private fb: FormBuilder,
    private candidateDisabilitiesService: CandidateDisabilitiesService,
    private route: ActivatedRoute,
    public translate: TranslateService,
    private notificationsService: NotificationsService,
  ) {
    this.formDocuments = this.fb.group({});
    this.es = calendarES();
  }

  ngOnInit() {
    this.candidateId = JSON.parse(localStorage.getItem("profile")).candidate_id;
    this.per_page = 10;
    this.loadCandidateDisabilities();
  }

  private getInsuranceCode(insurance?: string): string | null {
    if (!insurance) {
      return null;
    }

    const normalized = insurance.trim();
    const match = ListInsurances.find(
      (item) =>
        item.label.trim().toLowerCase() === normalized.toLowerCase() ||
        item.value.trim().toLowerCase() === normalized.toLowerCase(),
    );

    return match ? match.value : normalized;
  }

  public loadCandidateDisabilities(query?: string, filtersReceived?: any[]) {
    this.isLoading = true;
    this.candidateDisabilitiesService
      .getCandidateDisabilities(
        this.candidateId,
        this.page,
        this.per_page,
        this.sort,
        query,
        filtersReceived,
      )
      .toPromise()
      .then(
        (response) => {
          this.disabilities = response.json().data;
          this.totalDisabilities = response.json().meta.total;
          this.isLoading = false;
        },
        (error) => {
          console.error("Error fetching disabilities:", error);
          this.isLoading = false;
        },
      );
  }

  public showDocumentationDialog(
    documents: any[],
    disabilitie_type: string,
    isEdit: boolean = false,
    idDisability: number,
    insurance?: string,
  ) {
    this.selectedDisabilitie = disabilitie_type;
    this.idDisability = idDisability;
    this.dialogVisibilityKey = this.getInsuranceCode(insurance);
    this.formDocuments = this.fb.group({});
    const selectedDisability = this.documentsTypeDisabilities.find(
      (tipo) => tipo.key === disabilitie_type,
    );
    const formGroupKey = this.formGroupNameDocuments;

    if (selectedDisability) {
      const group = this.fb.group({});

      // Si el parámetro isEdit es verdadero, agregamos la propiedad 'edit' al FormGroup
      if (isEdit) {
        group.addControl("edit", this.fb.control("yes")); // Modo edición
        this.isEditSavedDocument = true;
      } else {
        group.addControl("edit", this.fb.control("no")); // No está en modo edición
        this.isEditSavedDocument = false;
      }

      // Agregar los FormArray como en el código original
      selectedDisability.typedocuments.forEach((doc) => {
        const formArrayName = doc.formNameArray;
        const formArray = this.fb.array([]);

        // Filtramos los documentos del backend que corresponden al tipo de documento actual
        const matchingDocuments = documents.filter(
          (existingDoc) =>
            existingDoc.list_documents_type_disabilities_id == doc.id,
        );

        if (matchingDocuments.length > 0) {
          // Si hay documentos que coinciden con el tipo, los agregamos al FormArray
          matchingDocuments.forEach((existingDoc) => {
            // Si estamos en modo edición, agregamos la propiedad 'edit' al documento
            if (isEdit) {
              existingDoc.edit = "yes"; // No se puede eliminar
            } else {
              existingDoc.edit = "no"; // Se puede eliminar
            }
            formArray.push(this.fb.control(existingDoc)); // Usamos el documento existente
          });
        } else {
          // Si no hay documentos, agregamos un FormControl vacío (no completado) para representar que no hay documento cargado
          formArray.push(this.fb.control({ id: 0, edit: "no" })); // Control vacío, pero con edit como 'no'
        }

        // Ahora, aseguramos que el FormArray sea obligatorio solo si tiene documentos nuevos (sin id ni edit)
        formArray.setValidators([
          (control: FormArray) => {
            // Verificamos si hay al menos un documento nuevo (sin id ni edit)
            const hasNewDocument = control.controls.some(
              (ctrl) => !ctrl.value.id && !ctrl.value.edit,
            );
            return hasNewDocument ? null : { required: true }; // Si tiene un documento nuevo, lo hace obligatorio
          },
        ]);

        // Agregamos el FormArray al grupo
        group.addControl(formArrayName, formArray);
      });

      // Finalmente, agregamos el grupo al FormGroup principal
      this.formDocuments.addControl(formGroupKey, group);
    }

    // Abrimos el diálogo de documentación
    this.modViewDialogDocumentation = true;
  }

  public saveAndExit() {
    if (this.formDocuments.valid) {
      this.createFormDataAndSave();
    } else {
      this.getFormValidationErrors(this.formDocuments);
    }
  }

  private createFormDataAndSave() {
    const formData = new FormData();

    // Accedemos al FormGroup correspondiente
    const documentsGroup = this.formDocuments.get(
      this.formGroupNameDocuments,
    ) as FormGroup;

    // Verificamos que documentsGroup sea válido
    if (documentsGroup) {
      // Iteramos sobre las claves del FormGroup
      Object.keys(documentsGroup.controls).forEach((key) => {
        const control = documentsGroup.get(key); // Obtenemos el control por clave

        // Si el control es un FormArray, lo procesamos
        if (control instanceof FormArray) {
          const formArray = control as FormArray;

          formArray.controls.forEach((file: FormControl) => {
            // Verificamos si el control tiene un archivo nuevo (sin 'id' ni 'edit')
            if (file.value && !file.value.id && !file.value.edit) {
              // Solo agregamos los nuevos documentos (archivos) al FormData
              formData.append(`documents[${key}][]`, file.value);
            }
          });
        }
      });
    }

    this.promiseForm = this.candidateDisabilitiesService
      .updateDisabilityDocumentCandidate(this.idDisability, formData)
      .toPromise()
      .then((response) => {
        this.handleErrorOrSuccess("success", "Guardado exitoso");
        this.loadCandidateDisabilities();
        // Cerramos el diálogo de documentación
        this.modViewDialogDocumentation = false;
      })
      .catch((error) => {
        this.handleErrorOrSuccess("error", "Error al guardar");
      });
  }

  private handleErrorOrSuccess(
    severity: "success" | "error",
    translateKey: string,
  ) {
    const message = this.translate.instant(translateKey);
    this.notificationsService.growlNotify(severity, message);
  }

  public getFormValidationErrors(form: FormGroup) {
    Object.keys(form.controls).forEach((key) => {
      const controlErrors = form.get(key).errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((keyError) => {
          console.error(
            `Key control: ${key}, keyError: ${keyError}, err value: ${controlErrors[keyError]}`,
          );
        });
      }
    });
  }

  getStatusLabel(lastDisabilitiesStatus: any): string {
    if (!lastDisabilitiesStatus || lastDisabilitiesStatus.status_id == null) {
      return "Has No State";
    }
    const statusId = lastDisabilitiesStatus.status_id.toString(); // Convertimos a string
    const status = this.StatusOptions.find(
      (option) => option.value === statusId,
    );

    return status ? status.label : ""; // Retornamos el label o vacío si no se encuentra
  }
}

// public createformFilter(data: any = null) {
//   data = data || {
//     typeOfDisability: '',
//     typeOfContingency: '',
//     typeInsurance: '',
//     dateStart: '',
//     dateEnd: '',
//     arlProvider: '',
//     epsProvider: '',
//   };
//   this.formFilter = this.fb.group({
//     typeOfDisability: new FormControl(data.typeOfDisability),
//     typeOfContingency: new FormControl(data.typeOfContingency),
//     typeInsurance: new FormControl(data.typeInsurance),
//     dateStart: new FormControl(data.dateStart),
//     dateEnd: new FormControl(data.dateEnd),
//     arlProvider: new FormControl(data.arlProvider),
//     epsProvider: new FormControl(data.epsProvider)
//   });
// }

// public filter(values) {
//   const filters = [];
//   if (values) {
//     Object.keys(values).forEach(key => {
//       let value = values[key];

//       if (key === 'dateStart' || key === 'dateEnd') {
//         const dateValue = new Date(value);
//         if (!isNaN(dateValue.getTime())) {
//           value = formatDate(dateValue);
//         } else {
//           value = null;
//         }
//       }

//       if (value) {
//         filters.push({ 'key': key, 'value': value });
//       }
//     });
//   }

//   this.loadCandidateDisabilities(undefined, filters);
// }

// public onInsuranceChange(value: string) {
//   this.selectedInsurance = value;
//   this.isArl = value === 'ARL';
//   this.isEps = value === 'EPS';
// }



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/disability/components/candidate-disabilities/candidate-disabilities.component.ts